import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import ExcelJS from 'exceljs'
import { isViewAllRole } from '@crm/shared'
import { useAuthStore } from '../auth/authStore'
import { getTableItems } from '../../shared/api/requestItems.api'
import { reportDataExport } from '../../shared/api/security.api'
import type { TableRow } from '../table/types'

// Колонки выгрузки — соответствуют блоку «Таблица» (basic-часть).
const COLUMNS: { header: string; field: keyof TableRow; width?: number }[] = [
  { header: 'Клиент', field: 'clientName', width: 28 },
  { header: 'Заявка', field: 'requestId', width: 16 },
  { header: 'Код', field: 'code', width: 12 },
  { header: 'Категория', field: 'clientCategory', width: 10 },
  { header: 'Дата', field: 'date', width: 12 },
  { header: 'Part Number', field: 'partNumber', width: 22 },
  { header: 'Бренд', field: 'brand', width: 18 },
  { header: 'Кол-во', field: 'qty', width: 10 },
  { header: 'Ед.', field: 'unit', width: 8 },
  { header: 'Сопровод', field: 'soprovod', width: 22 },
  { header: 'Приоритет', field: 'priority', width: 12 },
  { header: 'Срок КП', field: 'deadline', width: 12 },
  { header: 'Примечание', field: 'comments', width: 30 },
  { header: 'Целевая цена', field: 'targetPrice', width: 14 },
  { header: 'Статус', field: 'status', width: 14 },
]

const PAGE_LIMIT = 1000

async function fetchAllRows(
  onProgress: (loaded: number, total: number) => void,
): Promise<TableRow[]> {
  const all: TableRow[] = []
  let page = 1
  // Первая страница — узнаём total
  const first = await getTableItems(page, PAGE_LIMIT)
  all.push(...first.rows)
  onProgress(all.length, first.total)
  const totalPages = Math.ceil(first.total / PAGE_LIMIT)
  for (page = 2; page <= totalPages; page++) {
    const res = await getTableItems(page, PAGE_LIMIT)
    all.push(...res.rows)
    onProgress(all.length, first.total)
  }
  return all
}

async function buildWorkbook(rows: TableRow[]): Promise<Blob> {
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('База данных')

  ws.columns = COLUMNS.map((c) => ({ header: c.header, key: c.field, width: c.width ?? 14 }))

  // Шапка
  const headerRow = ws.getRow(1)
  headerRow.font = { bold: true }
  headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  headerRow.height = 24
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } }
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    }
  })

  // Данные
  rows.forEach((row) => {
    const vals: Record<string, unknown> = {}
    for (const col of COLUMNS) vals[col.field] = row[col.field] ?? ''
    ws.addRow(vals)
  })

  ws.views = [{ state: 'frozen', ySplit: 1 }]
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: COLUMNS.length },
  }

  const buf = await wb.xlsx.writeBuffer()
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

export function DatabasePage() {
  const { user } = useAuthStore()
  const role = user?.role
  // Раздел — только ADMIN и Генеральный директор: ровно как пункт меню
  // «База данных» в navConfig.tsx. Остальных уводим на дашборд.
  if (role && !isViewAllRole(role)) {
    return <Navigate to="/dashboard" replace />
  }

  return <DatabasePageInner />
}

function DatabasePageInner() {
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleExport = async () => {
    setBusy(true)
    setError(null)
    setProgress({ loaded: 0, total: 0 })
    try {
      const rows = await fetchAllRows((loaded, total) => setProgress({ loaded, total }))
      const blob = await buildWorkbook(rows)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const today = new Date().toISOString().split('T')[0]
      a.href = url
      a.download = `База данных ${today}.xlsx`
      a.click()
      URL.revokeObjectURL(url)
      // Журнал безопасности + алерт в TG. Сбой отметки не мешает самой выгрузке.
      void reportDataExport('database', rows.length).catch(() => {})
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сформировать выгрузку')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">База данных</h1>
        <p className="text-sm text-gray-500 mt-1">
          Ежедневная выгрузка всех позиций заявок (всех менеджеров) в Excel.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        <div className="text-sm text-gray-600">
          Формат: XLSX. Колонки соответствуют блоку «Таблица» (базовый набор без слотов закупщиков).
          Файл назван по текущей дате.
        </div>

        <button
          onClick={handleExport}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {busy ? 'Формирование…' : '⬇ Скачать Excel'}
        </button>

        {busy && progress && progress.total > 0 && (
          <div className="text-xs text-gray-500">
            Загружено {progress.loaded} / {progress.total} позиций
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-3 py-2">
            Ошибка: {error}
          </div>
        )}
      </div>
    </div>
  )
}
